import React, { useState, useEffect, useMemo } from 'react';
import patternsRadarService from '../../../services/patternsRadarService';
import DoubleScrollTable from './DoubleScrollTable';
import {
  formatPercent,
  formatCurrency,
  formatMultiplier,
  formatDecimal,
  toNum,
  matchesNumericRange,
} from '../patternsRadarUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faBookmark,
  faFileExport,
  faExclamationTriangle,
  faCheckCircle,
  faSpinner,
  faChartLine,
  faChevronLeft,
  faChevronRight,
  faBolt,
} from '@fortawesome/free-solid-svg-icons';

import PresetsModal from './PresetsModal';

const ScreenerStudio = ({ onOpenSymbol, onSendToBacktest, initialQuery }) => {
  const [source, setSource] = useState(
    initialQuery || 'close > ema(21) > ema(50) and volume > 2x avg(volume, 20)'
  );
  const [universe, setUniverse] = useState(500);
  const [asOf, setAsOf] = useState('');
  const [limit, setLimit] = useState(100);
  const [orderBy, setOrderBy] = useState('turnover');
  const [orderDir, setOrderDir] = useState('desc');

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errorInfo, setErrorInfo] = useState(null);

  // Table Column Filters (CRM Theme)
  const [filterSymbol, setFilterSymbol] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterPrice, setFilterPrice] = useState('');
  const [filter1D, setFilter1D] = useState('');
  const [filterDelivery, setFilterDelivery] = useState('');
  const [filterRelVol, setFilterRelVol] = useState('');
  const [filterRsi, setFilterRsi] = useState('');

  const hasActiveFilters = Boolean(
    filterSymbol || filterName || filterPrice || filter1D || filterDelivery || filterRelVol || filterRsi
  );

  const clearFilters = () => {
    setFilterSymbol('');
    setFilterName('');
    setFilterPrice('');
    setFilter1D('');
    setFilterDelivery('');
    setFilterRelVol('');
    setFilterRsi('');
  };

  const displayedRows = useMemo(() => {
    if (!result?.rows) return [];
    return result.rows.filter((row) => {
      if (filterSymbol && !row.symbol?.toLowerCase().includes(filterSymbol.toLowerCase().trim())) {
        return false;
      }
      if (filterName && !row.name?.toLowerCase().includes(filterName.toLowerCase().trim())) {
        return false;
      }
      if (!matchesNumericRange(row.close, filterPrice)) {
        return false;
      }
      if (filter1D === 'gainers' && (toNum(row.ret_1d) ?? 0) <= 0) {
        return false;
      }
      if (filter1D === 'losers' && (toNum(row.ret_1d) ?? 0) >= 0) {
        return false;
      }
      if (filterDelivery && (toNum(row.delivery_pct) ?? 0) < Number(filterDelivery)) {
        return false;
      }
      if (filterRelVol && (toNum(row.rel_volume) ?? 0) < Number(filterRelVol)) {
        return false;
      }
      if (filterRsi === 'overbought' && (toNum(row.rsi_14) ?? 0) < 70) {
        return false;
      }
      if (filterRsi === 'oversold' && (toNum(row.rsi_14) ?? 0) > 30) {
        return false;
      }
      if (filterRsi === 'neutral') {
        const rsi = toNum(row.rsi_14) ?? 50;
        if (rsi < 30 || rsi > 70) return false;
      }
      return true;
    });
  }, [result?.rows, filterSymbol, filterName, filterPrice, filter1D, filterDelivery, filterRelVol, filterRsi]);

  // Results Table Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);

  useEffect(() => {
    setPage(1);
  }, [result?.rows, filterSymbol, filterName, filterPrice, filter1D, filterDelivery, filterRelVol, filterRsi]);

  const totalPages = Math.ceil(displayedRows.length / pageSize) || 1;
  const start = displayedRows.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, displayedRows.length);

  const paginatedRows = useMemo(() => {
    return displayedRows.slice((page - 1) * pageSize, page * pageSize);
  }, [displayedRows, page, pageSize]);

  // Saved Scans & Strategy Presets
  const [savedScans, setSavedScans] = useState([]);
  const [loadingScans, setLoadingScans] = useState(false);

  // Save Modal state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState('');

  const fetchSavedScans = async () => {
    setLoadingScans(true);
    try {
      const res = await patternsRadarService.listSavedScans();
      setSavedScans(res?.scans || []);
    } catch (e) {
      console.error('Failed to load saved scans in ScreenerStudio:', e);
    } finally {
      setLoadingScans(false);
    }
  };

  useEffect(() => {
    fetchSavedScans();
  }, []);

  useEffect(() => {
    if (initialQuery) {
      setSource(initialQuery);
      setActiveStrategyTitle('');
    }
  }, [initialQuery]);

  // Deduplicate saved scans to prevent any duplicate entries
  const uniqueSavedScans = useMemo(() => {
    if (!Array.isArray(savedScans)) return [];
    const seenIds = new Set();
    const seenCombos = new Set();
    const list = [];

    for (const scan of savedScans) {
      if (!scan || !scan.id) continue;
      if (seenIds.has(scan.id)) continue;

      const normQuery = (scan.source || '').trim().replace(/\s+/g, ' ').toLowerCase();
      const normName = (scan.name || '').trim().toLowerCase();
      const combo = `${normName}::${normQuery}`;

      if (seenCombos.has(combo)) continue;

      seenIds.add(scan.id);
      seenCombos.add(combo);
      list.push(scan);
    }

    return list;
  }, [savedScans]);

  const [showPresetsModal, setShowPresetsModal] = useState(false);
  const [activeStrategyTitle, setActiveStrategyTitle] = useState('');

  const handleApplyPreset = (item) => {
    if (!item) return;
    setSource(item.query);
    setActiveStrategyTitle(item.name);
    if (item.universe) {
      setUniverse(item.universe);
    }
  };

  const handleSourceChange = (e) => {
    setSource(e.target.value);
    setActiveStrategyTitle('');
  };

  const handleRunScan = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setErrorInfo(null);
    setSaveSuccessMsg('');

    try {
      const payload = {
        source: source.trim(),
        universe: Number(universe),
        limit: Number(limit),
        orderBy,
        orderDir,
        kinds: ['equity'],
      };
      if (asOf) payload.asOf = asOf;

      const data = await patternsRadarService.runScan(payload);

      if (data.ok === false) {
        // Compiler refused query
        setErrorInfo({
          type: 'compiler',
          message: data.error || 'Syntax error in Sift query',
          hint: data.hint,
          line: data.line,
          col: data.col,
        });
        setResult(null);
      } else {
        setResult(data);
      }
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.title ||
        err.message ||
        'Failed to execute scan';
      setErrorInfo({
        type: 'network',
        message: detail,
        status: err.response?.status,
      });
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveScan = async () => {
    if (!saveName.trim()) return;
    setSaving(true);
    try {
      const newScan = await patternsRadarService.saveScan({
        name: saveName.trim(),
        source: source.trim(),
        universe: Number(universe),
      });
      setShowSaveModal(false);
      setSaveName('');
      setSaveSuccessMsg(`Strategy "${newScan?.name || saveName.trim()}" saved to presets!`);
      setTimeout(() => setSaveSuccessMsg(''), 4000);

      // Refresh saved scans to immediately update dropdown
      const res = await patternsRadarService.listSavedScans().catch(() => null);
      if (res?.scans) {
        setSavedScans(res.scans);
      }
      if (newScan?.name || saveName.trim()) {
        setActiveStrategyTitle(newScan?.name || saveName.trim());
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save scan.');
    } finally {
      setSaving(false);
    }
  };

  const handleExportCSV = () => {
    if (!result?.rows || result.rows.length === 0) return;
    const headers = [
      'Symbol',
      'Name',
      'Close',
      '1D Chg%',
      'Volume',
      'Turnover (Cr)',
      'Delivery%',
      'Rel Vol',
      'RSI 14',
      '52W High',
      '52W Low',
    ];
    const rows = result.rows.map((r) => [
      r.symbol,
      `"${r.name || ''}"`,
      formatDecimal(r.close, { decimals: 2 }),
      formatDecimal(r.ret_1d, { decimals: 2 }),
      r.volume,
      (r.turnover / 10000000).toFixed(2),
      formatDecimal(r.delivery_pct, { decimals: 2 }),
      formatDecimal(r.rel_volume, { decimals: 2 }),
      formatDecimal(r.rsi_14, { decimals: 1 }),
      formatDecimal(r.high_52w, { decimals: 2 }),
      formatDecimal(r.low_52w, { decimals: 2 }),
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `street_analysis_scan_${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      {/* Query Studio Panel */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-heading border-0 d-flex justify-content-between align-items-center flex-wrap gap-2 py-2">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="panel-title font-weight-bold d-flex align-items-center text-nowrap mb-0">
              <FontAwesomeIcon icon={faPlay} className="text-primary me-2" />
              Sift Quantitative Query Studio
            </span>

            {/* Strategy Presets Library Button & Active Indicator */}
            <div className="d-inline-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-primary btn-xs d-inline-flex align-items-center font-weight-bold"
                onClick={() => setShowPresetsModal(true)}
                title="Open Strategies & Presets Library (142 built-in + saved)"
              >
                <FontAwesomeIcon icon={faBolt} className="me-1" />
                <span>Strategies</span>
              </button>

              {activeStrategyTitle && (
                <div
                  className="badge bg-light text-dark border d-inline-flex align-items-center gap-1 text-truncate py-1 px-2"
                  style={{ maxWidth: '260px', fontSize: '11px', height: '26px' }}
                  title={`Active Strategy: ${activeStrategyTitle}`}
                >
                  <span className="text-muted">Strategy:</span>
                  <strong className="text-primary">{activeStrategyTitle}</strong>
                </div>
              )}

              {loadingScans && (
                <FontAwesomeIcon icon={faSpinner} spin className="text-muted small" />
              )}
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-default btn-xs"
              onClick={() => setShowSaveModal(true)}
              title="Save to Library"
            >
              <FontAwesomeIcon icon={faBookmark} className="me-1" />
              Save Scan
            </button>
            <button
              className="btn btn-default btn-xs"
              onClick={() => onSendToBacktest && onSendToBacktest(source)}
              title="Send to Hit Rate Strategy Backtester"
            >
              <FontAwesomeIcon icon={faChartLine} className="me-1 text-success" />
              Send to Backtest
            </button>
          </div>
        </div>

        <div className="panel-body">
          {/* Sift Query Textarea */}
          <div className="mb-3">
            <textarea
              className="pr-query-box"
              value={source}
              onChange={handleSourceChange}
              placeholder="Enter Sift condition e.g.: close > ema(21) and volume > 2x avg(volume, 20)"
            />
          </div>

          {/* Compiler or Network Error Alert */}
          {errorInfo && (
            <div className="alert alert-danger py-2 px-3 small d-flex align-items-center mb-3">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="me-2 text-danger fs-5"
              />
              <div>
                <strong>Query Error:</strong> {errorInfo.message}
                {errorInfo.hint && (
                  <span className="ms-2 badge bg-warning text-dark">
                    Hint: {errorInfo.hint}
                  </span>
                )}
                {errorInfo.line && (
                  <span className="ms-2 text-muted">
                    (Line {errorInfo.line}, Col {errorInfo.col})
                  </span>
                )}
              </div>
            </div>
          )}

          {saveSuccessMsg && (
            <div className="alert alert-success py-2 px-3 small mb-3">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              {saveSuccessMsg}
            </div>
          )}

          {/* Parameters Bar */}
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label className="form-label small text-muted mb-1 font-weight-bold">
                Universe Scope
              </label>
              <select
                className="form-control form-control-sm"
                value={universe}
                onChange={(e) => setUniverse(e.target.value)}
              >
                <option value="100">Top 100 Equities</option>
                <option value="500">Top 500 Equities (Standard)</option>
                <option value="2000">Top 2000 Liquid Equities</option>
                <option value="9999">All NSE Equities (Full DB)</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label small text-muted mb-1 font-weight-bold">
                As Of Date (Optional)
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={asOf}
                onChange={(e) => setAsOf(e.target.value)}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label small text-muted mb-1 font-weight-bold">
                Sort By
              </label>
              <select
                className="form-control form-control-sm"
                value={orderBy}
                onChange={(e) => setOrderBy(e.target.value)}
              >
                <option value="turnover">Turnover (Value)</option>
                <option value="volume">Volume</option>
                <option value="ret_1d">1D Return %</option>
                <option value="delivery_pct">Delivery %</option>
                <option value="rel_volume">Relative Volume</option>
                <option value="rsi_14">RSI (14)</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label small text-muted mb-1 font-weight-bold">
                Direction / Max
              </label>
              <div className="d-flex gap-1">
                <select
                  className="form-control form-control-sm"
                  value={orderDir}
                  onChange={(e) => setOrderDir(e.target.value)}
                >
                  <option value="desc">Desc</option>
                  <option value="asc">Asc</option>
                </select>
                <select
                  className="form-control form-control-sm"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                >
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>

            <div className="col-md-3 text-end">
              <button
                type="button"
                className="btn btn-primary btn-sm px-4 font-weight-bold"
                onClick={handleRunScan}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlay} className="me-1" />
                    Execute Scan
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Table Panel */}
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="panel-heading border-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <span className="panel-title font-weight-bold">
              Screening Results
            </span>
            {result && (
              <span className="ms-2 badge bg-success text-white">
                {result.count} Matches Found
              </span>
            )}
            {hasActiveFilters && (
              <span className="ms-2 badge bg-info text-white">
                Filtered: {displayedRows.length} shown
              </span>
            )}
            {result?.elapsedMs && (
              <span className="ms-2 text-muted small">
                ({result.elapsedMs}ms compile time | As of: {result.asOf})
              </span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {result?.rows && result.rows.length > 0 && (
              <>
                <span className="small text-muted">
                  Showing <strong>{start}</strong> to <strong>{end}</strong> of <strong>{displayedRows.length}</strong>
                </span>
                {displayedRows.length > 100 && (
                  <div className="pagination-nav-buttons ms-1">
                    <button
                      type="button"
                      className="btn btn-default btn-xs nav-prev-btn"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      title="Previous"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-default btn-xs nav-next-btn"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      title="Next"
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                )}
              </>
            )}
            {result?.rows && result.rows.length > 0 && (
              <button
                className="btn btn-default btn-xs"
                onClick={handleExportCSV}
              >
                <FontAwesomeIcon icon={faFileExport} className="me-1" />
                Export CSV
              </button>
            )}
          </div>
        </div>

        <div className="panel-body p-0">
          <DoubleScrollTable dependencies={[paginatedRows]}>
            <table className="table table-hover table-condensed bg-white m-b-0 sticky-table pr-table">
              <thead>
                <tr className="bg-light">
                  <th style={{ minWidth: '120px' }}>Symbol</th>
                  <th style={{ minWidth: '180px' }}>Company Name</th>
                  <th style={{ minWidth: '100px' }} className="text-end">Last Price</th>
                  <th style={{ minWidth: '110px' }} className="text-end">1D Change</th>
                  <th style={{ minWidth: '120px' }} className="text-end">Turnover (₹ Cr)</th>
                  <th style={{ minWidth: '110px' }} className="text-end">Delivery %</th>
                  <th style={{ minWidth: '90px' }} className="text-end">Rel Vol</th>
                  <th style={{ minWidth: '110px' }} className="text-end">RSI (14)</th>
                  <th style={{ minWidth: '120px' }} className="text-center">52W Position</th>
                  <th style={{ minWidth: '80px' }} className="text-center">Action</th>
                </tr>
                <tr>
                  <th>
                    <input
                      type="text"
                      placeholder="Search..."
                      value={filterSymbol}
                      onChange={(e) => setFilterSymbol(e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="Search..."
                      value={filterName}
                      onChange={(e) => setFilterName(e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="e.g. 100-500"
                      title="Enter single value or range (e.g. 100-500, >=100, <=500)"
                      value={filterPrice}
                      onChange={(e) => setFilterPrice(e.target.value)}
                    />
                  </th>
                  <th>
                    <select
                      value={filter1D}
                      onChange={(e) => setFilter1D(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="gainers">Gainers (&gt;0%)</option>
                      <option value="losers">Losers (&lt;0%)</option>
                    </select>
                  </th>
                  <th></th>
                  <th>
                    <select
                      value={filterDelivery}
                      onChange={(e) => setFilterDelivery(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="50">&gt;= 50%</option>
                      <option value="60">&gt;= 60%</option>
                      <option value="70">&gt;= 70%</option>
                    </select>
                  </th>
                  <th>
                    <select
                      value={filterRelVol}
                      onChange={(e) => setFilterRelVol(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="1.5">&gt; 1.5x</option>
                      <option value="2.0">&gt; 2.0x</option>
                      <option value="3.0">&gt; 3.0x</option>
                    </select>
                  </th>
                  <th>
                    <select
                      value={filterRsi}
                      onChange={(e) => setFilterRsi(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="oversold">Oversold (&lt;30)</option>
                      <option value="neutral">Neutral (30-70)</option>
                      <option value="overbought">Overbought (&gt;70)</option>
                    </select>
                  </th>
                  <th></th>
                  <th className="text-center">
                    {hasActiveFilters ? (
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-clear-filter"
                        onClick={clearFilters}
                        title="Clear filters"
                      >
                        Clear
                      </button>
                    ) : null}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        spin
                        className="me-2 text-primary fs-5"
                      />
                      Compiling Sift query & screening NSE instruments...
                    </td>
                  </tr>
                ) : paginatedRows.length > 0 ? (
                  paginatedRows.map((row, idx) => {
                    // Compute 52W range progress percentage
                    const rangeSpan = row.high_52w - row.low_52w;
                    const rangePct =
                      rangeSpan > 0
                        ? Math.min(
                            100,
                            Math.max(
                              0,
                              ((row.close - row.low_52w) / rangeSpan) * 100
                            )
                          )
                        : 50;

                    return (
                      <tr key={idx}>
                        <td>
                          <button
                            type="button"
                            className="btn btn-link p-0 font-weight-bold text-primary"
                            onClick={() => onOpenSymbol && onOpenSymbol(row.symbol)}
                          >
                            {row.symbol}
                          </button>
                        </td>
                        <td
                          className="text-truncate"
                          style={{ maxWidth: '180px' }}
                          title={row.name}
                        >
                          {row.name || '-'}
                        </td>
                        <td className="text-end font-weight-bold">
                          {formatCurrency(row.close)}
                        </td>
                        <td
                          className={`text-end ${
                            (toNum(row.ret_1d) ?? 0) >= 0 ? 'pr-positive' : 'pr-negative'
                          }`}
                        >
                          {formatPercent(row.ret_1d, { showPlus: true })}
                        </td>
                        <td className="text-end">
                          ₹{toNum(row.turnover) !== null ? (row.turnover / 10000000).toFixed(2) : '0.00'} Cr
                        </td>
                        <td className="text-end">
                          <span
                            className={`badge ${
                              toNum(row.delivery_pct) >= 50
                                ? 'bg-success'
                                : 'bg-light text-dark'
                            }`}
                          >
                            {formatPercent(row.delivery_pct, { showPlus: false })}
                          </span>
                        </td>
                        <td className="text-end">
                          {formatMultiplier(row.rel_volume)}
                        </td>
                        <td className="text-end">
                          <span
                            className={
                              toNum(row.rsi_14) >= 70
                                ? 'text-danger font-weight-bold'
                                : toNum(row.rsi_14) <= 30
                                ? 'text-success'
                                : ''
                            }
                          >
                            {formatDecimal(row.rsi_14, { decimals: 1 })}
                          </span>
                        </td>
                        <td className="text-center">
                          <div
                            className="pr-range-container"
                            title={`52W Low: ${formatCurrency(row.low_52w)} | 52W High: ${formatCurrency(row.high_52w)}`}
                          >
                            <div
                              className="pr-range-indicator"
                              style={{ left: `${rangePct}%` }}
                            />
                          </div>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-primary btn-xs"
                            onClick={() =>
                              onOpenSymbol && onOpenSymbol(row.symbol)
                            }
                            title={`View ${row.symbol} Chart & Technicals`}
                          >
                            <FontAwesomeIcon icon={faChartLine} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">
                      {result?.rows && result.rows.length > 0 && hasActiveFilters
                        ? 'No stocks match the active table filters. (Click "Clear" to reset filters)'
                        : result
                        ? 'No stocks matched the query criteria in this universe.'
                        : 'Click "Execute Scan" to run the screener.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </DoubleScrollTable>

          {/* Footer Pagination Bar */}
          {displayedRows.length > 100 && (
            <div className="d-flex justify-content-between align-items-center p-2 border-top bg-light flex-wrap gap-2">
              <div className="small text-muted">
                Showing <strong>{start}</strong> to <strong>{end}</strong> of <strong>{displayedRows.length}</strong> matches
              </div>
              <div className="pagination-nav-buttons">
                <button
                  type="button"
                  className="btn btn-default btn-xs nav-prev-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  title="Previous"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                  type="button"
                  className="btn btn-default btn-xs nav-next-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  title="Next"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Scan Modal */}
      {showSaveModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title font-weight-bold">
                  Save Scan to Library
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSaveModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label small font-weight-bold">
                    Scan Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Delivery Surge with EMA Breakout"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    maxLength={120}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label small text-muted">
                    Sift Formula
                  </label>
                  <div className="p-2 bg-light rounded small text-monospace">
                    {source}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-default btn-sm"
                  onClick={() => setShowSaveModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveScan}
                  disabled={saving || !saveName.trim()}
                >
                  {saving ? 'Saving...' : 'Save Scan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Presets & Strategy Library Modal (Option 1) */}
      <PresetsModal
        isOpen={showPresetsModal}
        onClose={() => setShowPresetsModal(false)}
        onSelectPreset={handleApplyPreset}
        savedScans={uniqueSavedScans}
        currentQuery={source}
      />
    </div>
  );
};

export default ScreenerStudio;
